import React from "react";
import { Box, Typography, Avatar, AvatarGroup } from "@mui/material";
import { useTheme, keyframes } from "@mui/material/styles";
import { useTranslation } from "react-i18next";

const PHOTO_BASE = "https://d1unewf2oslns8.cloudfront.net/tnfm-walkup";
const PHOTO_IDS = [
  "67a28918cb93cdf2916a6913.jpeg",
  "67a2891ccb93cdf2916a6951.jpeg",
  "64db101b502099bda9629e17.jpeg",
  "64db1028502099bda9629e23.jpeg",
  "67a2891bcb93cdf2916a693f.jpeg",
  "67a2891acb93cdf2916a6933.jpeg",
];
const photoUrl = (id) => `${PHOTO_BASE}/${id}`;

// Decorative captions only - these are stock photos, not real attendees.
const FAN = [
  { tx: -128, ty: 20, rot: -13, z: 1, delay: "0s", label: "M · 28" },
  { tx: -66, ty: 5, rot: -6, z: 2, delay: ".6s", label: "J · 31" },
  { tx: 0, ty: -2, rot: 0, z: 3, delay: "1.1s", label: "A · 26", center: true },
  { tx: 66, ty: 5, rot: 6, z: 2, delay: ".3s", label: "S · 29" },
  { tx: 128, ty: 20, rot: 13, z: 1, delay: ".9s", label: "D · 30" },
];

const floatY = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-7px); }
`;
const softPulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: .45; transform: scale(.8); }
`;

const CountChip = ({ count }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  if (!count) return null;
  return (
    <Box
      aria-live="polite"
      sx={{
        position: "absolute",
        bottom: -2,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 5,
        display: "flex",
        alignItems: "center",
        gap: "7px",
        background: "#fff",
        border: "1px solid #F0E6E0",
        borderRadius: "999px",
        padding: "7px 14px 7px 12px",
        boxShadow: "0 8px 20px rgba(60,30,18,.16)",
        whiteSpace: "nowrap",
      }}
    >
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: theme.palette.primary.main,
          animation: `${softPulse} 1.8s ease-in-out infinite`,
          "@media (prefers-reduced-motion: reduce)": { animation: "none" },
        }}
      />
      <Typography sx={{ font: "700 13px Bitter, serif", color: "#211B19" }}>
        {t("walkup.crowd.count", { count })}
      </Typography>
    </Box>
  );
};

// `group` (compact AvatarGroup) is used on the returning-user variant; `fan`
// (the full fanned cards) is the primary new-user hero.
const CrowdFan = ({ count = null, variant = "fan" }) => {
  const { t } = useTranslation();

  if (variant === "group") {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mt: 1 }}>
        <AvatarGroup max={5} sx={{ "& .MuiAvatar-root": { width: 40, height: 40, border: "2px solid #fff" } }}>
          {PHOTO_IDS.slice(0, 5).map((id) => (
            <Avatar key={id} src={photoUrl(id)} alt="" imgProps={{ loading: "lazy" }} />
          ))}
        </AvatarGroup>
        {count ? (
          <Typography sx={{ font: "700 13px Bitter, serif", color: "#211B19", mt: 1.2 }}>
            {t("walkup.crowd.count", { count })}
          </Typography>
        ) : null}
      </Box>
    );
  }

  return (
    <Box sx={{ px: 3, pt: 1 }}>
      <Box
        aria-hidden="true"
        sx={{
          position: "relative",
          height: 172,
          margin: "14px 0 2px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {FAN.map((c, i) => (
          <Box
            key={i}
            sx={{
              position: "absolute",
              transform: `translateX(${c.tx}px) translateY(${c.ty}px) rotate(${c.rot}deg)`,
              zIndex: c.z,
            }}
          >
            <Box
              sx={{
                animation: `${floatY} 5s ease-in-out infinite`,
                animationDelay: c.delay,
                "@media (prefers-reduced-motion: reduce)": { animation: "none" },
                width: c.center ? 96 : 90,
                height: c.center ? 122 : 114,
                borderRadius: c.center ? "19px" : "18px",
                backgroundColor: "#E7C8B4",
                backgroundImage: `url(${photoUrl(PHOTO_IDS[i])})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                boxShadow: c.center
                  ? "0 16px 30px rgba(60,30,18,.22)"
                  : "0 10px 22px rgba(60,30,18,.18)",
                display: "flex",
                alignItems: "flex-end",
                padding: c.center ? "10px" : "9px",
              }}
            >
              <Box
                sx={{
                  background: "rgba(255,255,255,.85)",
                  borderRadius: "9px",
                  padding: "3px 8px",
                  font: "700 11px Bitter, serif",
                  color: "#5A4A40",
                }}
              >
                {c.label}
              </Box>
            </Box>
          </Box>
        ))}
        <CountChip count={count} />
      </Box>
      <Typography
        sx={{ textAlign: "center", font: "500 12.5px Bitter, serif", color: "#A1968F", px: 4 }}
      >
        {t("walkup.crowd.caption")}
      </Typography>
    </Box>
  );
};

export default CrowdFan;
